// Generated from scripts/component-cart-items.ts. Rebuild with pnpm scripts:build.
var _a;
import { Component } from '@theme/component';
import { CartSectionRestoredEvent, QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { orderCartRows, recordAddedVariants } from '@theme/cart-line-order';
import { ANNOUNCEMENT_DURATION, cartSectionIds, isAjaxCart, readCart, withTimeout } from '@theme/product-form';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer, startViewTransition } from '@theme/utilities';
import { CartDiscountUpdateEvent, CartErrorEvent, CartLinesUpdateEvent, CartNoteUpdateEvent, StandardEvents, createViewEventElement, } from '@shopify/events';
/** Names the writes of this module in the detail of the cart events. */
const SOURCE = 'cart-items-component';
/** A write that has not been answered by then is treated as lost: the cart may or may not have applied it. */
const CHANGE_TIMEOUT = 15000;
/** How long a stepper rests before its quantity is sent, so typing or pressing plus twice makes one write. */
const QUANTITY_DELAY = 300;
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function isAbort(error) {
    return error instanceof Error && error.name === 'AbortError';
}
function sectionsOf(body) {
    const { sections } = body;
    if (!isRecord(sections))
        return undefined;
    const values = Object.values(sections);
    return values.every((entry) => typeof entry === 'string' || entry === null) ? sections : undefined;
}
/** What Shopify says when it refuses a change: a description, or an `errors` sentence from the older answer. */
function refusalOf(body) {
    if (!isRecord(body))
        return { message: '', description: '' };
    const { message, description, errors } = body;
    return {
        message: typeof message === 'string' ? message : '',
        description: typeof description === 'string' ? description : typeof errors === 'string' ? errors : '',
    };
}
function hasErrors(body) {
    return 'errors' in body && Boolean(body.errors);
}
function lineIn(cart, key) {
    return cart.items.find((item) => item.key === key);
}
/** Posts the change. A server error, a timeout, a network failure and a body that is not JSON are all `lost`. */
async function postChange(body) {
    const timeout = withTimeout(CHANGE_TIMEOUT);
    try {
        const response = await fetch(Theme.routes.cart_change_url, {
            ...fetchConfig('json', { body: JSON.stringify(body) }),
            signal: timeout.signal,
        });
        if (response.status >= 500)
            return { kind: 'lost' };
        const parsed = await response.json();
        return { kind: 'answered', ok: response.ok, status: response.status, body: parsed };
    }
    catch {
        return { kind: 'lost' };
    }
    finally {
        timeout.stop();
    }
}
/** One parent edit and its attached quantities settle as one cart event. Every child write uses a fresh key. */
async function postPersonalizedChange(key, quantity, sections) {
    let cart = await readCart();
    const original = cart?.items.find((line) => line.key === key);
    const group = original?.properties?._wontio_group;
    if (!cart || !original || typeof group !== 'string')
        return { kind: 'lost' };
    const parentKey = (line) => {
        if (!isRecord(line))
            return undefined;
        const relation = line.parent_relationship;
        return isRecord(relation) && typeof relation.parent_key === 'string' ? relation.parent_key : undefined;
    };
    const children = cart.items.filter((line) => parentKey(line) === key).map((line) => ({ variant: line.variant_id, units: Number(line.properties?._wontio_units) }));
    const refusal = () => ({ kind: 'answered', ok: false, status: 422, body: { description: document.querySelector(`[data-key="${CSS.escape(key)}"]`)?.dataset.personalizationError ?? '' } });
    if (children.some((child) => !Number.isSafeInteger(child.units) || child.units < 1))
        return refusal();
    const parentAnswer = await postChange({ id: key, quantity, sections, sections_url: window.location.pathname });
    if (parentAnswer.kind === 'answered' && !parentAnswer.ok)
        return parentAnswer;
    cart = parentAnswer.kind === 'answered' && isAjaxCart(parentAnswer.body) ? parentAnswer.body : await readCart();
    if (!cart)
        return { kind: 'lost' };
    if (quantity === 0)
        return cart.items.some((line) => line.properties?._wontio_group === group) ? { kind: 'lost' } : { kind: 'answered', ok: true, status: 200, body: cart };
    const currentParent = (value) => value.items.find((line) => line.properties?._wontio_group === group && line.variant_id === original.variant_id);
    let parent = currentParent(cart);
    if (!parent)
        return { kind: 'lost' };
    // A lost parent response is followed only by an authoritative read, never a second parent write.
    if (parentAnswer.kind === 'lost' && parent.quantity !== quantity)
        return { kind: 'lost' };
    for (const expected of children) {
        parent = currentParent(cart);
        const child = cart.items.find((line) => parent && parentKey(line) === parent.key && line.variant_id === expected.variant);
        if (!parent || !child)
            return refusal();
        const wanted = parent.quantity * expected.units;
        if (child.quantity === wanted)
            continue;
        const answer = await postChange({ id: child.key, quantity: wanted, sections, sections_url: window.location.pathname });
        cart = answer.kind === 'answered' && answer.ok && isAjaxCart(answer.body) ? answer.body : await readCart();
        if (!cart)
            return { kind: 'lost' };
        parent = currentParent(cart);
        const confirmed = cart.items.find((line) => parent && parentKey(line) === parent.key && line.variant_id === expected.variant);
        if (confirmed?.quantity !== wanted)
            return refusal();
    }
    return { kind: 'answered', ok: true, status: 200, body: cart };
}
/**
 * Changes the quantity of one cart line and publishes the result. The `CartLinesUpdateEvent` is dispatched once,
 * before the request; its promise settles with the cart as Shopify reports it (with `didError` when the cart
 * refused), or rejects when nothing can be said about the cart. Renders in flight for the cart sections are
 * cancelled first, so a stale render cannot land over the answer.
 */
export async function changeCartLine({ source, key, quantity }) {
    const sections = cartSectionIds();
    for (const id of sections.split(',')) {
        if (id)
            sectionRenderer.abortRender(id);
    }
    const deferred = CartLinesUpdateEvent.createPromise();
    // A rejection nobody listens for is not an error of the page: the listeners that care attach their own handlers.
    deferred.promise.catch(() => undefined);
    source.dispatchEvent(new CartLinesUpdateEvent({
        action: quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: key, quantity }],
        promise: deferred.promise,
    }));
    /** Tells the listeners about a cart that holds the change. */
    const settleChanged = (cart, answered, recovered) => {
        deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { sections: answered, items: cart.items, itemCount: cart.item_count, source: SOURCE, didError: false },
        });
        const group = personalized instanceof HTMLElement ? personalized.dataset.personalizationGroup : undefined;
        const applied = lineIn(cart, key)?.quantity ?? (group ? cart.items.find((line) => line.properties?._wontio_group === group)?.quantity : undefined);
        return { status: 'changed', cart, sections: answered, recovered, applied };
    };
    /** The cart after an answer that did not settle it: whether it already holds the change decides the outcome. */
    const holdsChange = (cart) => {
        if (!cart)
            return false;
        const line = lineIn(cart, key);
        return quantity === 0 ? line === undefined : line?.quantity === quantity;
    };
    const personalized = document.querySelector(`[data-key="${CSS.escape(key)}"][data-personalization]`);
    const answer = personalized ? await postPersonalizedChange(key, quantity, sections) : await postChange({ id: key, quantity, sections, sections_url: window.location.pathname });
    if (answer.kind === 'answered' && answer.ok && isRecord(answer.body) && isAjaxCart(answer.body) && !hasErrors(answer.body)) {
        return settleChanged(answer.body, sectionsOf(answer.body), false);
    }
    if (answer.kind === 'answered' && answer.status !== 400) {
        // The cart decided: this is a refusal. It is read anyway, so every quantity on the page agrees with it.
        const refusal = refusalOf(answer.body);
        source.dispatchEvent(new CartErrorEvent({
            error: refusal.description || refusal.message || 'Cart update failed',
            code: 'INVALID',
            detail: { description: refusal.description },
        }));
        const cart = await readCart();
        if (cart) {
            deferred.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: { items: cart.items, itemCount: cart.item_count, source: SOURCE, didError: true },
            });
        }
        else {
            deferred.reject(new Error(refusal.description || refusal.message || 'Cart update failed'));
        }
        return { status: 'rejected', httpStatus: answer.status, ...refusal };
    }
    // No answer, or the cart does not know the key: read the cart before saying anything about the change.
    const cart = await readCart();
    if (holdsChange(cart))
        return settleChanged(cart, undefined, true);
    source.dispatchEvent(new CartErrorEvent({ error: 'Network error during cart update', code: 'SERVICE_UNAVAILABLE' }));
    deferred.reject(new Error('The cart update did not complete.'));
    return answer.kind === 'answered' ? { status: 'stale' } : { status: 'lost' };
}
/** The hook of the rows' message slot: the polite region, the message and the retry control. */
const MESSAGE = {
    slot: '[data-line-message]',
    body: '[data-line-message-body]',
    text: '[data-line-message-text]',
    retry: '[data-line-message-retry]',
};
function isTextComponent(value) {
    return value instanceof HTMLElement && 'shimmer' in value && typeof value.shimmer === 'function';
}
function hasUpdateCartQuantity(element) {
    return 'updateCartQuantity' in element && typeof element.updateCartQuantity === 'function';
}
function hasUpdateButtonStates(element) {
    return 'updateButtonStates' in element && typeof element.updateButtonStates === 'function';
}
function isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
}
/**
 * The cart's lines, in the drawer and on the cart page: quantity steppers, removal, and the rendering of every
 * cart change from the section Shopify returns with it.
 *
 * Writes. A stepper rests for 300 ms and then sends the quantity its line should hold; a remove button sends 0. Both
 * go through `changeCartLine` with the line's key, in the order they were made: `enqueue` runs one write of this
 * list at a time, so rapid edits of one or several lines apply in sequence and a change to a line that has
 * gone since is dropped. While writes are pending the list ignores pointers and the totals shimmer. A removed line
 * collapses at once and stays in the document until the cart confirms it; if the write fails it opens again.
 *
 * Failures. A refusal (a stock limit) puts Shopify's sentence in the line's message slot and the stepper back to
 * the quantity the cart confirmed. A quantity the cart lowered by itself says what it became. A lost answer says the
 * cart did not change and offers to try again, which is safe because a change sets an absolute quantity. A key the
 * cart no longer knows refreshes the list.
 *
 * Rendering. A change made by another module (an add, the gift wrap, a variant swap) arrives as a cart event and the
 * list morphs the section it carries. `holdRendering` pauses that for a module that makes several changes in a row
 * and wants one render at the end.
 *
 * Contract kept from the inherited module: the `cart-items-component` element and its `data-section-id` and
 * `data-drawer` attributes, the `cartItemRows[]`, `quantitySelectors[]` and `cartTotal` refs, the `onLineItemRemove`
 * handler the remove buttons are bound to, `pendingQuantityUpdates`, and the events it listens to and publishes. The
 * per-line `cartItemError-N` refs became the row's message slot (`data-line-message`).
 */
export class CartItemsComponent extends createViewEventElement(Component) {
    static #pendingQuantityUpdates = new Set();
    static #holds = 0;
    static #heldComponents = new Set();
    /**
     * Includes quantity writes, their section renders and control cleanup across all cart instances.
     * @returns A snapshot that cannot modify the shared pending set.
     */
    static get pendingQuantityUpdates() {
        return Array.from(_a.#pendingQuantityUpdates);
    }
    /**
     * Keeps every list from rendering and from remembering added lines until the returned function is called. The
     * newest section a held change brought is rendered then, so a module that adds a line and removes another shows
     * the result once and not the step in between. A change that failed brings nothing, and the render of an earlier
     * step stands. Holds nest; the lists render when the last one is released, and the promise settles when they have.
     */
    static holdRendering() {
        _a.#holds += 1;
        let released = false;
        return async () => {
            if (released)
                return;
            released = true;
            _a.#holds -= 1;
            if (_a.#holds > 0)
                return;
            const components = Array.from(_a.#heldComponents);
            _a.#heldComponents.clear();
            await Promise.all(components.map((component) => component.#renderHeld()));
        };
    }
    #lifetime;
    #queue = Promise.resolve();
    #busy = 0;
    #quantityTimers = new Map();
    #retries = new Map();
    #held;
    #announceTimer = 0;
    /**
     * Every render path (add, quantity change, discount, note, restore and external app renders) replaces rows in
     * Shopify's order; the observer puts the most recently added line back on top after each one. A reorder that
     * changes nothing re-enters once and stops.
     */
    #lineOrderObserver = new MutationObserver(() => orderCartRows(this));
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        const { signal } = lifetime;
        document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
        document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
        document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onDiscountUpdate, { signal });
        document.addEventListener(StandardEvents.cartNoteUpdate, this.#onNoteUpdate, { signal });
        window.addEventListener('pageshow', this.#onPageShow, { signal });
        this.#lineOrderObserver.observe(this, { childList: true, subtree: true });
        orderCartRows(this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.#lineOrderObserver.disconnect();
        for (const timer of this.#quantityTimers.values())
            window.clearTimeout(timer);
        this.#quantityTimers.clear();
        window.clearTimeout(this.#announceTimer);
        _a.#heldComponents.delete(this);
        this.#held = undefined;
    }
    /** The section this list renders from. */
    get sectionId() {
        const { sectionId } = this.dataset;
        if (!sectionId)
            throw new Error('Section id missing');
        return sectionId;
    }
    /** Whether the list is the cart drawer's. */
    get isDrawer() {
        return this.dataset.drawer !== undefined;
    }
    /* ----- finding lines ----- */
    get #rows() {
        const rows = this.refs.cartItemRows;
        return Array.isArray(rows) ? rows.filter((row) => row instanceof HTMLElement) : [];
    }
    /**
     * The row of a Shopify line number. Rows are looked up by their line, not their position, because the list is
     * shown most recently added first. The reduced gift wrap rows carry a line number and a key without being rows of
     * the list.
     */
    #rowForLine(line) {
        return Array.from(this.querySelectorAll('[data-key][data-line]')).find((row) => Number(row.dataset.line) === line);
    }
    #rowByKey(key) {
        return this.querySelector(`[data-key="${CSS.escape(key)}"]`) ?? undefined;
    }
    /** The row and the rows nested under it, which leave with it. */
    #groupOf(row) {
        const { key } = row.dataset;
        return [row, ...this.#rows.filter((candidate) => key !== undefined && candidate.dataset.parentKey === key)];
    }
    /* ----- writes ----- */
    /**
     * Runs `task` after every write queued before it, and keeps the list busy meanwhile. Steppers, remove buttons and
     * a module that writes several lines in a row (the variant swap) share this queue, so no two writes of one list
     * overlap and the order they were made in is the order they apply in.
     */
    enqueue(task) {
        const result = this.#queue.then(task);
        this.#queue = result.catch(() => undefined);
        const settled = result.then(() => undefined, () => undefined);
        _a.#pendingQuantityUpdates.add(settled);
        this.#setBusy(1);
        void settled.then(() => {
            _a.#pendingQuantityUpdates.delete(settled);
            this.#setBusy(-1);
        });
        return result;
    }
    #setBusy(change) {
        this.#busy += change;
        this.classList.toggle('cart-items-disabled', this.#busy > 0);
    }
    #onQuantityUpdate = (event) => {
        if (!(event instanceof QuantitySelectorUpdateEvent) || !(event.target instanceof Node) || !this.contains(event.target))
            return;
        const { quantity, cartLine } = event.detail;
        // Cart items require a line number.
        if (!cartLine)
            return;
        const key = this.#rowForLine(cartLine)?.dataset.key;
        if (!key)
            return;
        window.clearTimeout(this.#quantityTimers.get(key));
        this.#quantityTimers.set(key, window.setTimeout(() => {
            this.#quantityTimers.delete(key);
            if (quantity === 0)
                this.#remove(key).catch(this.#logFailure);
            else
                this.enqueue(() => this.#applyQuantity(key, quantity)).catch(this.#logFailure);
        }, QUANTITY_DELAY));
    };
    /** Removes a line. The remove buttons are bound to this with their line number. */
    onLineItemRemove(line) {
        const key = this.#rowForLine(line)?.dataset.key;
        if (key)
            this.#remove(key).catch(this.#logFailure);
    }
    /** A write that throws is a defect of the page and not an answer of the cart: it is logged and the list unlocks. */
    #logFailure = (error) => {
        console.warn('[cart-items] The cart update failed:', error);
    };
    /** Sends `change` for a line and reports it, with the totals and the line's price shimmering until it settles. */
    async #change(key, quantity) {
        const marker = cartPerformance.createStartingMarker(`${quantity === 0 ? 'clear' : 'change'}:user-action`);
        const cartTotal = this.refs.cartTotal;
        if (isTextComponent(cartTotal))
            cartTotal.shimmer();
        const price = this.#rowByKey(key)?.querySelector('text-component');
        if (quantity > 0 && isTextComponent(price))
            price.shimmer();
        try {
            return await changeCartLine({ source: this, key, quantity });
        }
        finally {
            resetShimmer(this);
            cartPerformance.measureFromMarker(marker);
        }
    }
    async #applyQuantity(key, quantity) {
        const row = this.#rowByKey(key);
        if (!row)
            return;
        this.#clearMessage(row);
        const outcome = await this.#change(key, quantity);
        if (outcome.status === 'changed') {
            await this.#renderChanged(outcome.cart, outcome.sections);
            const { applied } = outcome;
            if (applied !== undefined && applied !== quantity) {
                this.showLineMessage(key, (this.dataset.quantityAdjustedText ?? '').replace('[quantity]', String(applied)));
            }
            return;
        }
        // Back to the quantity the cart last confirmed, which the server rendered as the input's value.
        const input = this.#stepperOf(key);
        if (input)
            input.value = input.defaultValue;
        this.#updateButtonStates();
        this.#reportFailure(key, outcome, () => {
            const stepper = this.#stepperOf(key);
            if (stepper)
                stepper.value = String(quantity);
            this.#updateButtonStates();
            this.enqueue(() => this.#applyQuantity(key, quantity)).catch(this.#logFailure);
        });
    }
    #stepperOf(key) {
        return this.#rowByKey(key)?.querySelector('input[data-cart-line]') ?? null;
    }
    async #remove(key) {
        const row = this.#rowByKey(key);
        if (!row)
            return;
        this.#clearMessage(row);
        const group = this.#groupOf(row);
        // The cart is empty once every line leaves; the section Shopify renders then replaces the list with the empty state.
        const emptiesCart = this.#rows.every((candidate) => group.includes(candidate));
        const neighbour = this.#neighbourOf(group);
        const hadFocus = group.some((candidate) => candidate.contains(document.activeElement));
        const expand = this.#collapse(group);
        await this.enqueue(async () => {
            const outcome = await this.#change(key, 0);
            if (outcome.status === 'changed') {
                await this.#renderChanged(outcome.cart, outcome.sections, emptiesCart ? this.#emptyTransition() : undefined);
                if (hadFocus && neighbour)
                    this.#focusRemove(neighbour);
                return;
            }
            expand();
            this.#reportFailure(key, outcome, () => this.#remove(key).catch(this.#logFailure));
            if (hadFocus)
                this.#focusRemove(key);
        });
    }
    /** Collapses the rows the way a removal leaves, before the cart confirms it; the returned function opens them again. */
    #collapse(rows) {
        for (const row of rows) {
            row.setAttribute('aria-busy', 'true');
            if (prefersReducedMotion())
                continue;
            row.style.setProperty('--row-height', `${row.clientHeight}px`);
            row.classList.add('removing');
        }
        return () => {
            for (const row of rows) {
                row.classList.remove('removing');
                row.style.removeProperty('--row-height');
                row.removeAttribute('aria-busy');
            }
        };
    }
    /** The key of the row that takes the focus when a group of rows leaves: the next one, else the one before. */
    #neighbourOf(group) {
        const rows = this.#rows.filter((row) => !row.dataset.parentKey);
        const index = rows.findIndex((row) => group.includes(row));
        const next = rows.slice(index + 1).find((row) => !group.includes(row));
        const previous = rows.slice(0, Math.max(index, 0)).reverse().find((row) => !group.includes(row));
        return (next ?? previous)?.dataset.key;
    }
    #focusRemove(key) {
        this.#rowByKey(key)?.querySelector('[data-line-remove]')?.focus({ preventScroll: true });
    }
    /** The view transition type of the cart losing its last line, which depends on where the list sits. */
    #emptyTransition() {
        return [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page'];
    }
    /* ----- failures ----- */
    /** Says in the line's message slot why a change did not happen, with a way to try again when trying again is safe. */
    #reportFailure(key, outcome, retry) {
        const failed = this.dataset.updateFailedText ?? '';
        switch (outcome.status) {
            case 'rejected':
                this.showLineMessage(key, outcome.description || outcome.message || failed);
                break;
            case 'lost':
                this.showLineMessage(key, failed, { retry });
                break;
            case 'stale':
                // The cart changed under the list; the section shows what it holds now. The line may have gone with it.
                void this.reconcile().then(() => {
                    if (!this.showLineMessage(key, failed))
                        this.announce(failed);
                });
                break;
            default: {
                const exhaustive = outcome;
                throw new Error(`Unhandled cart line outcome ${String(exhaustive)}`);
            }
        }
    }
    /**
     * Writes a message into the slot of a line, which is a polite region that exists before the message does, so it
     * is announced once. `retry` shows a control that runs it. Returns false when the line has no slot to write in.
     */
    showLineMessage(key, message, options = {}) {
        const row = this.#rowByKey(key);
        const slot = row?.querySelector(MESSAGE.slot);
        const body = slot?.querySelector(MESSAGE.body);
        const text = slot?.querySelector(MESSAGE.text);
        const retryButton = slot?.querySelector(MESSAGE.retry);
        if (!slot || !body || !text || !message)
            return false;
        text.textContent = message;
        body.hidden = false;
        if (retryButton)
            retryButton.hidden = options.retry === undefined;
        if (options.retry)
            this.#retries.set(key, options.retry);
        else
            this.#retries.delete(key);
        slot.scrollIntoView({ block: 'nearest' });
        return true;
    }
    #clearMessage(row) {
        const body = row.querySelector(`${MESSAGE.slot} ${MESSAGE.body}`);
        const text = row.querySelector(`${MESSAGE.slot} ${MESSAGE.text}`);
        if (body)
            body.hidden = true;
        if (text)
            text.textContent = '';
        if (row.dataset.key)
            this.#retries.delete(row.dataset.key);
    }
    /** Runs the retry a message offered. The retry control of a line is bound to this. */
    retryLine(event) {
        const key = event.target instanceof Element ? event.target.closest('[data-key]')?.dataset.key : undefined;
        if (!key)
            return;
        const retry = this.#retries.get(key);
        this.#retries.delete(key);
        retry?.();
    }
    /** Speaks through the list's polite region: the confirmed outcome of a change the lines themselves cannot show. */
    announce(message) {
        const region = this.querySelector('[data-cart-status]');
        if (!region || !message)
            return;
        region.textContent = '';
        window.requestAnimationFrame(() => {
            region.textContent = message;
        });
        window.clearTimeout(this.#announceTimer);
        this.#announceTimer = window.setTimeout(() => {
            region.textContent = '';
        }, ANNOUNCEMENT_DURATION);
    }
    /* ----- rendering ----- */
    /**
     * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
     * @param options Additional section morph options.
     */
    async #refreshCartSection(options = {}) {
        try {
            await sectionRenderer.renderSection(this.sectionId, {
                cache: false,
                mode: this.isDrawer ? 'hydration' : 'full',
                ...options,
            });
            this.#updateButtonStates();
        }
        catch (error) {
            if (isAbort(error))
                return;
            this.dispatchEvent(new CartErrorEvent({
                error: error instanceof Error && error.message ? error.message : 'Failed to refresh cart',
                code: 'SERVICE_UNAVAILABLE',
            }));
        }
    }
    /** Renders the list from the server. A module that could not tell what the cart holds calls it to show the truth. */
    reconcile() {
        return this.#refreshCartSection();
    }
    /** Renders the answer of this list's own change: its section when it came with one, else a fresh section. */
    async #renderChanged(cart, sections, transition) {
        this.#updateQuantitySelectors(cart);
        const html = sections?.[this.sectionId];
        if (typeof html !== 'string') {
            await this.#refreshCartSection();
            return;
        }
        sectionRenderer.abortRender(this.sectionId);
        const morph = () => morphSection(this.sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full' });
        await (transition ? startViewTransition(morph, transition) : morph());
        this.#updateButtonStates();
    }
    /**
     * True when the event was dispatched from outside this cart-items-component (e.g.
     * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
     * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
     * `this` and either morph the section themselves or don't need a refresh — running
     * a fallback render in that case double-renders and can clobber form state.
     */
    #isExternalCartUpdate(event) {
        return !(event.target instanceof Node) || !this.contains(event.target);
    }
    #onDiscountUpdate = (event) => {
        if (!(event instanceof CartDiscountUpdateEvent))
            return;
        const isExternal = this.#isExternalCartUpdate(event);
        event.promise
            ?.then(({ detail }) => {
            if (!isExternal)
                return;
            const html = detail?.sections?.[this.sectionId];
            if (typeof html === 'string') {
                sectionRenderer.abortRender(this.sectionId);
                void morphSection(this.sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full' }).then(() => this.#updateButtonStates());
            }
            else {
                // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't attach sections;
                // refetch so the discount UI reflects the post-mutation cart.
                void this.#refreshCartSection();
            }
        })
            .catch((error) => {
            if (!isAbort(error))
                console.warn('[cart-items] Event promise rejected:', error);
        });
    };
    #onNoteUpdate = (event) => {
        // Internal cart-note dispatches don't need a section refresh — the user typed the value and the textarea
        // retains it. Only external callers need the UI synced.
        if (!(event instanceof CartNoteUpdateEvent) || !this.#isExternalCartUpdate(event))
            return;
        event.promise
            ?.then(({ detail }) => {
            const html = detail?.sections?.[this.sectionId];
            if (typeof html === 'string') {
                sectionRenderer.abortRender(this.sectionId);
                void morphSection(this.sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full' });
            }
            else {
                void this.#refreshCartSection();
            }
        })
            .catch((error) => {
            if (!isAbort(error))
                console.warn('[cart-items] Event promise rejected:', error);
        });
    };
    /**
     * Handles a cart change made outside this list, or by a module inside it: the list renders the section the
     * change returned. The change of this list's own steppers is rendered by the code that wrote it.
     */
    #onCartLinesUpdate = (event) => {
        if (event.target === this || !(event instanceof CartLinesUpdateEvent))
            return;
        event.promise
            ?.then(async ({ detail }) => {
            await this.#renderCartUpdate(event, detail);
        })
            .catch((error) => {
            if (!isAbort(error))
                console.warn('[cart-items] Event promise rejected:', error);
        });
    };
    async #renderCartUpdate(event, detail) {
        const html = detail?.sections?.[this.sectionId];
        const sectionHtml = typeof html === 'string' ? html : undefined;
        // A change that failed leaves the cart as the list already shows it.
        if (_a.#holds > 0) {
            if (!detail?.didError) {
                this.#held = { html: sectionHtml };
                _a.#heldComponents.add(this);
            }
            return;
        }
        // Animate empty → non-empty in the drawer (possible in squeeze mode where the page is interactive alongside the
        // open drawer). This also needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        const morphOptions = {
            mode: this.isDrawer ? 'hydration' : 'full',
            injectStylesheet: wasEmptyCartDrawer,
        };
        // Remembered before the morph so the re-rendered list already comes out newest first.
        if (event.action === 'add' && !detail?.didError) {
            recordAddedVariants(event.lines.map((line) => line.merchandiseId));
        }
        if (sectionHtml === undefined) {
            await this.#refreshCartSection(morphOptions);
            return;
        }
        sectionRenderer.abortRender(this.sectionId);
        const existingKeys = new Set(this.#rows.map((row) => row.dataset.key));
        if (wasEmptyCartDrawer) {
            await startViewTransition(() => morphSection(this.sectionId, sectionHtml, morphOptions), ['fill-cart-drawer']);
        }
        else {
            await morphSection(this.sectionId, sectionHtml, morphOptions);
            // A re-added variant merges into its existing line, so the morph changes no child list and the observer
            // stays quiet; the line still moves up.
            orderCartRows(this);
        }
        // Animate newly added rows (reverse of the remove animation).
        if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.#rows) {
                if (!existingKeys.has(row.dataset.key)) {
                    row.classList.add('adding');
                    void onAnimationEnd(row, () => row.classList.remove('adding'));
                }
            }
        }
        // Update button states for all cart quantity selectors after morph.
        this.#updateButtonStates();
    }
    /** Renders what a hold kept back: the newest section a held change brought, or a fresh section when it brought none. */
    async #renderHeld() {
        const held = this.#held;
        this.#held = undefined;
        if (!held || !this.isConnected)
            return;
        if (held.html === undefined) {
            await this.#refreshCartSection({ injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null });
            return;
        }
        const html = held.html;
        sectionRenderer.abortRender(this.sectionId);
        await morphSection(this.sectionId, html, {
            mode: this.isDrawer ? 'hydration' : 'full',
            injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
        });
        orderCartRows(this);
        this.#updateButtonStates();
    }
    /**
     * Re-renders the cart section when the page is restored from the back/forward cache.
     *
     * A restored page replays a frozen DOM, so the line items still show whatever the cart held before the shopper
     * navigated away. `cart-icon.js` self-corrects its count bubble on this same event, which is why the bubble and
     * the cart body disagree until a reload.
     *
     * `persisted` alone does not cover Chrome. Chrome refuses the bfcache for any document holding the Sign in with
     * Shop iframe, `/services/login_with_shop/authorize` inside the shadow root of `shopify-account > shop-login`, and
     * every product page carries one. A shopper's Back there rebuilds the document from the HTTP disk cache instead:
     * `persisted` is false and the frozen cart markup renders anyway. Only the navigation entry separates that from
     * an ordinary page view, so it is the second channel. Safari and Firefox do restore the same page and arrive
     * through `persisted`. Measured on os3 across all three browsers, 2026-09-03.
     *
     * Both channels are still needed. A restored document keeps its ORIGINAL navigation entry in Chrome and Safari,
     * reporting `navigate`, so `back_forward` alone would miss every real restore. And `pageshow` fires after `load`
     * on every ordinary navigation, so dropping both guards would put a section fetch on every page view.
     */
    #onPageShow = (event) => {
        const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
        const navigationType = isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
        if (!event.persisted && navigationType !== 'back_forward')
            return;
        // `section_id` is optional in `snippets/cart-items-component.liquid` and the `sectionId` getter throws without
        // it. Every other caller reads it inside a promise chain that already has a catch; this one runs straight off a
        // window listener, so it would escape.
        if (!this.dataset.sectionId)
            return;
        // `cache: false` on both paths. A bfcache restore never fires `load`, so the renderer's cache still holds the
        // pre-navigation copy. A disk-cache Back does fire `load` and seeds the cache from the stale document it just
        // rebuilt. Either way the cached copy is the same stale HTML the DOM already shows.
        sectionRenderer
            .renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
            // Empty → non-empty adds the cart summary markup, which carries its own stylesheet.
            injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
        })
            .then(() => {
            // Morph swaps the quantity selectors in place, so they never reconnect and their min/max button states stay
            // bound to the pre-restore quantities. A line restored to its minimum would otherwise keep minus enabled: a
            // wrong affordance and a wasted cart mutation.
            this.#updateButtonStates();
            // No drawer-open event fires on a restore, so the sticky summary measurement has to be driven from here.
            // `cart-drawer-component` listens for this.
            this.dispatchEvent(new CartSectionRestoredEvent());
        })
            .catch((error) => {
            if (!isAbort(error))
                console.warn('[cart-items] bfcache restore render failed:', error);
        });
    };
    /** Updates quantity selectors for all matching variants in the cart. */
    #updateQuantitySelectors(cart) {
        for (const item of cart.items) {
            const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${item.variant_id}"]`);
            for (const selector of selectors) {
                const input = selector.querySelector('input[data-cart-quantity]');
                if (!input)
                    continue;
                input.setAttribute('data-cart-quantity', item.quantity.toString());
                // Update the quantity selector's internal state.
                if (hasUpdateCartQuantity(selector))
                    selector.updateCartQuantity();
            }
        }
    }
    /** Updates button states for all cart quantity selector components. */
    #updateButtonStates() {
        for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
            if (hasUpdateButtonStates(selector))
                selector.updateButtonStates();
        }
    }
}
_a = CartItemsComponent;
if (!customElements.get('cart-items-component')) {
    customElements.define('cart-items-component', CartItemsComponent);
}
